import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import BrandLogo from "@/components/feature/BrandLogo";
import TripBadge from "@/components/feature/TripBadge";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { navLinks } = useSiteSettings();

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <BrandLogo imgClassName="h-10 md:h-12" />

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.label}
                to={link.href}
                aria-current={active ? "page" : undefined}
                className={`text-sm font-medium transition-colors whitespace-nowrap px-3.5 py-2 rounded-full ${
                  active
                    ? scrolled
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-50/25 text-background-50"
                    : scrolled
                      ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      : "text-background-100 hover:text-background-50 hover:bg-background-50/10"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <TripBadge scrolled={scrolled} />
          <Link
            to="/farm-partner"
            aria-current={isActive("/farm-partner") ? "page" : undefined}
            className={`text-sm font-semibold px-4 py-2.5 rounded-md transition-colors whitespace-nowrap ${
              isActive("/farm-partner")
                ? scrolled
                  ? "bg-primary-100 text-primary-700"
                  : "bg-background-50/25 text-background-50"
                : scrolled
                  ? "text-foreground-900 hover:bg-background-200"
                  : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            For Farmers
          </Link>
          <Link
            to="/experiences"
            className="bg-accent-500 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            Explore Experiences
          </Link>
        </div>

        <button
          className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            scrolled ? "text-foreground-950" : "text-background-50"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-5 space-y-1">
          <TripBadge mobile onNavigate={() => setMobileOpen(false)} />
          {navLinks.map((link) => {
            const active = isActive(link.href);
            return (
              <Link
                key={link.label}
                to={link.href}
                aria-current={active ? "page" : undefined}
                className={`flex items-center justify-between text-sm font-medium py-3 px-3 rounded-md border-b border-background-100 ${
                  active
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
                {active && (
                  <i className="ri-checkbox-blank-circle-fill text-[8px] text-primary-500"></i>
                )}
              </Link>
            );
          })}
          <div className="pt-4 flex flex-col gap-3">
            <Link
              to="/farm-partner"
              onClick={() => setMobileOpen(false)}
              aria-current={isActive("/farm-partner") ? "page" : undefined}
              className={`text-sm font-semibold text-center py-3 rounded-md border ${
                isActive("/farm-partner")
                  ? "bg-primary-100 text-primary-700 border-primary-200"
                  : "text-foreground-900 border-background-300"
              }`}
            >
              For Farmers
            </Link>
            <Link
              to="/experiences"
              onClick={() => setMobileOpen(false)}
              className="bg-accent-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md text-center whitespace-nowrap"
            >
              Explore Experiences
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}